/* global DEVELOPMENT, STAGE */
/* eslint-disable no-param-reassign */
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { Reducer } from 'redux';

interface EventState {
  id: string;
  name: string;
  shortcode: string;
  description: string;
  timeFormat: '12 Hour' | '24 Hour';
  timezone: string;

  creationDate: string;
  startDate: string;
  endDate: string;

  language: string;
  alternativeLanguages: string[];

  locationAddress: string;
  locationName: string;

  partnerId: number;
  organizationId: string;
  eventFormat: 'virtual' | 'onsite' | 'hybrid' | null;
  eventType: 'default' | string; // TODO: Set this to the restricted types
  eventWebsite: string;
  eventIconImageId: string;
  isTrial: boolean;
  multilingualEventApp: boolean;
  perPersonLimit: number;
  supportEmailAddress: string;
  outboundEmailAddress: string | null;
  useCustomOutboundEmailAddress: boolean;
  customDomainEnabled: boolean;
  customDomainName: string | null;
  customDomainStatus: string | null;

  eventUrls: string[];
}

const generateEventUrls = (eventCode, customDomain = undefined) => {
  const urls = [`https://eventmobi.com/${eventCode}`];

  if (customDomain) {
    urls.push(`https://${customDomain}/${eventCode}`);
  }

  if (DEVELOPMENT) {
    urls.push(`https://app.localhost/${eventCode}`);
  }

  if (STAGE) {
    urls.push(`https://release.eventmobi.com/${eventCode}`);
  }

  return urls;
};

const initialState: Partial<EventState> = {
  id: null,
  name: null,
  shortcode: null,
  description: null,

  timeFormat: '12 Hour',
  timezone: 'America/New_York',

  creationDate: null,
  startDate: null,
  endDate: null,

  language: 'english',
  alternativeLanguages: [],

  locationAddress: null,
  locationName: null,

  partnerId: 0,
  organizationId: null,
  eventFormat: null,
  eventType: null,
  eventWebsite: null,
  eventIconImageId: null,
  isTrial: false,
  multilingualEventApp: null,
  perPersonLimit: null,
  supportEmailAddress: null,
  outboundEmailAddress: null,
  useCustomOutboundEmailAddress: null,
  customDomainEnabled: false,
  customDomainName: null,
  customDomainStatus: null,

  eventUrls: [],
};

const slice = createSlice({
  name: 'event',
  initialState,
  reducers: {
    setEvent: {
      prepare(event: any = {}) {
        const payload = {
          id: event.id,
          name: event.name,
          shortcode: event.shortcode,
          description: event.description,
          timeFormat: event.timeFormat,
          timezone: event.configuration?.timezone,
          creationDate: event.creationDate,
          startDate: event.startDate,
          endDate: event.endDate,
          language: event.language,
          alternativeLanguages: event.alternativeLanguages,
          locationAddress: event.locationAddress,
          locationName: event.locationName,
          partnerId: event.partnerId,
          organizationId: event.organizationId,
          eventFormat: event.eventFormat,
          eventType: event.eventType,
          eventWebsite: event.eventWebsite,
          eventIconImageId: event.eventIconImageId,
          isTrial: event.isTrial,
          multilingualEventApp: event.multilingualEventapp, // fixed the case
          perPersonLimit: event.perPersonLimit,
          supportEmailAddress: event.supportEmailAddress,
          outboundEmailAddress: event.outboundEmailAddress,
          useCustomOutboundEmailAddress: event.useCustomOutboundEmailAddress,
          customDomainEnabled: event.customDomainEnabled,
          customDomainName: event.customDomainName,
          customDomainStatus: event.customDomainStatus,
        };

        return { payload, meta: null, error: null };
      },
      reducer(state, { payload }) {
        state.id = payload.id;
        state.name = payload.name;
        state.shortcode = payload.shortcode;
        state.description = payload.description;
        state.timeFormat = payload.timeFormat;
        state.timezone = payload.timezone;
        state.creationDate = payload.creationDate;
        state.startDate = payload.startDate;
        state.endDate = payload.endDate;
        state.language = payload.language;
        state.alternativeLanguages = payload.alternativeLanguages;
        state.locationAddress = payload.locationAddress;
        state.locationName = payload.locationName;
        state.partnerId = payload.partnerId;
        state.organizationId = payload.organizationId;
        state.eventFormat = payload.eventFormat;
        state.eventType = payload.eventType;
        state.eventWebsite = payload.eventWebsite;
        state.eventIconImageId = payload.eventIconImageId;
        state.isTrial = payload.isTrial;
        state.multilingualEventApp = payload.multilingualEventApp;
        state.perPersonLimit = payload.perPersonLimit;
        state.supportEmailAddress = payload.supportEmailAddress;
        state.outboundEmailAddress = payload.outboundEmailAddress;
        state.useCustomOutboundEmailAddress = payload.useCustomOutboundEmailAddress;
        state.customDomainEnabled = payload.customDomainEnabled;
        state.customDomainName = payload.customDomainName;
        state.customDomainStatus = payload.customDomainStatus;
      },
    },

    setCustomDomain(state, { payload: { isCustomDomainEnabledConf, customDomainName } }) {
      if (isCustomDomainEnabledConf) {
        state.eventUrls = generateEventUrls(state.shortcode, customDomainName);
      } else {
        state.eventUrls = generateEventUrls(state.shortcode);
      }
    },
  },
});

// Explicit reducer annotation avoids TS2742 during declaration emit after the RTK 2 upgrade.
const { reducer }: { reducer: Reducer<Partial<EventState>> } = slice;

export default reducer;

export const getEvent = (state: EventState): EventState => state[slice.name];
const getEventState = (state: EventState) => state[slice.name];

export const getId = (state: EventState): string => state[slice.name]?.id;
export const getName = (state: EventState) => state[slice.name]?.name;
export const getShortcode = (state: EventState) => state[slice.name]?.shortcode;
export const getTimezone = (state: EventState) => state[slice.name]?.timezone;
export const getEventUrls = (state: EventState) => state[slice.name]?.eventUrls;
export const getSupportEmailAddress = (state: EventState) => state[slice.name]?.supportEmailAddress;

export const getDates = createSelector([getEventState], event => ({
  start: event?.startDate,
  end: event?.endDate,
}));

export const getLocation = createSelector([getEventState], event => ({
  address: event?.locationAddress,
  name: event?.locationName,
}));

export const { setEvent, setCustomDomain } = slice.actions;
